import { type ReactNode } from 'react';

/**
 * Tiny, dependency-free markdown renderer for Domina's replies. Handles the
 * formatting she actually uses — **bold**, *italic*, `code`, [links](url),
 * bullet lists and simple headings — and renders everything as real React text
 * nodes (no dangerouslySetInnerHTML), so raw ** / #### never leak to the user.
 */
function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)|(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))/g;
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1]) nodes.push(<strong key={key++}>{m[2]}</strong>);
    else if (m[3]) nodes.push(<em key={key++}>{m[4]}</em>);
    else if (m[5])
      nodes.push(
        <code key={key++} className="rounded bg-black/5 px-1 py-0.5 text-[0.85em]">
          {m[6]}
        </code>,
      );
    else if (m[7])
      nodes.push(
        <a key={key++} href={m[9]} target="_blank" rel="noopener noreferrer" className="underline">
          {m[8]}
        </a>,
      );
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function MarkdownLite({ content }: { content: string }) {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  let key = 0;

  const flush = () => {
    if (list.length) {
      const items = list;
      blocks.push(
        <ul key={key++} className="my-1 list-disc space-y-0.5 pl-4">
          {items.map((li, i) => (
            <li key={i}>{renderInline(li)}</li>
          ))}
        </ul>,
      );
      list = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    if (bullet) {
      list.push(bullet[1] ?? '');
      continue;
    }
    flush();
    if (!line.trim()) continue; // blank line → block spacing
    const heading = line.match(/^#{1,6}\s+(.*)$/);
    if (heading) {
      blocks.push(
        <p key={key++} className="mt-1 font-semibold">
          {renderInline(heading[1] ?? '')}
        </p>,
      );
      continue;
    }
    blocks.push(<p key={key++}>{renderInline(line)}</p>);
  }
  flush();

  return <div className="space-y-1">{blocks}</div>;
}
